/**
 * useParentCompanies Hook
 *
 * Fetches all customers to be used as parent company options.
 * Used in EditBasicInfoModal and CustomerFormWizard for parent company selection.
 *
 * Uses a single shared cache key so all components share the same API response.
 * Filtering by excludeCustomerId is done in memory, not via separate API calls.
 */

import { useRequest } from 'ahooks';
import { customerApi } from '@/services/api/customer.api';
import { useMemo } from 'react';

export interface ParentCompanyOption {
  companyId: number;
  companyName: string;
  customerCode: string;
}

/**
 * Hook to fetch all customers as potential parent companies
 * @param excludeCustomerId - Optional customer ID to exclude from the list (e.g., current customer being edited)
 */
export const useParentCompanies = (excludeCustomerId?: number) => {
  // Single API call with shared cache - no excludeCustomerId in cache key
  const { data, loading, error, refresh } = useRequest(
    () => customerApi.list({
      isActive: 1, // Only active customers
      pageSize: -1, // Get all customers (API spec: -1 = all)
    }),
    {
      cacheKey: '/api/customers-options', // Same cache key as useCustomerOptions
      staleTime: 60000, // 1 minute cache
    }
  );

  // Filter and transform in memory - this runs on cached data, no extra API call
  const parentCompanies = useMemo(() => {
    if (!data?.data) return [];

    return data.data
      .filter((customer) => {
        // Exclude the current customer if provided
        if (excludeCustomerId && customer.customerId === excludeCustomerId) {
          return false;
        }
        return true;
      })
      .map((customer) => ({
        companyId: customer.customerId,
        companyName: customer.customerName,
        customerCode: customer.customerCode,
      }));
  }, [data, excludeCustomerId]);

  return {
    parentCompanies,
    loading,
    error,
    refresh,
  };
};

export default useParentCompanies;
